import {
  Box,
  Paper,
  Typography,
  Link,
  Stack,
  useTheme,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import AppleIcon from "@mui/icons-material/Apple";
import AndroidIcon from "@mui/icons-material/Android";
import LanguageIcon from "@mui/icons-material/Language";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DashboardIcon from "@mui/icons-material/Dashboard";
import TimelineIcon from "@mui/icons-material/Timeline";
import DescriptionIcon from "@mui/icons-material/Description";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InsightsIcon from "@mui/icons-material/Insights";
import PsychologyIcon from "@mui/icons-material/Psychology";
import HubIcon from "@mui/icons-material/Hub";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

const GUIA_PAGINAS = [
  {
    titulo: "Dashboard",
    icone: <DashboardIcon color="primary" />,
    descricao:
      "Tela principal para acompanhar os dados mais recentes do ambiente selecionado.",
    quandoUsar: [
      "Visualizar rapidamente a condição atual de um ambiente.",
      "Conferir valores instantâneos, faixas de leitura e situação geral.",
      "Acompanhar o comportamento atual antes de aprofundar a análise.",
    ],
    pontosAtencao: [
      "Use o Dashboard como leitura operacional inicial.",
      "Para investigação de comportamento ao longo do tempo, utilize Histórico, Estatísticas ou IA.",
    ],
  },
  {
    titulo: "CCO",
    icone: <HubIcon color="primary" />,
    descricao:
      "Visão centralizada do sistema para acompanhar vários ambientes ao mesmo tempo.",
    quandoUsar: [
      "Monitorar múltiplos locais simultaneamente.",
      "Identificar rapidamente ambientes em pior condição.",
      "Obter uma leitura executiva e centralizada do sistema.",
    ],
    pontosAtencao: [
      "Ideal para supervisão geral e priorização de atendimento.",
      "Após identificar um local crítico, aprofunde a análise nas telas de Dashboard, Histórico ou IA.",
    ],
  },
  {
    titulo: "Histórico",
    icone: <TimelineIcon color="primary" />,
    descricao:
      "Permite consultar séries temporais e comparar o comportamento das variáveis em diferentes períodos.",
    quandoUsar: [
      "Investigar oscilações e tendências no tempo.",
      "Comparar dias, horários ou eventos específicos.",
      "Avaliar o efeito de ventilação, ocupação ou intervenções no ambiente.",
    ],
    pontosAtencao: [
      "Selecione períodos coerentes para facilitar a leitura dos gráficos.",
      "Períodos muito extensos podem reduzir a clareza visual dos dados.",
    ],
  },
  {
    titulo: "Relatórios",
    icone: <DescriptionIcon color="primary" />,
    descricao:
      "Gera relatórios a partir de períodos e parâmetros selecionados para consulta, registro e compartilhamento.",
    quandoUsar: [
      "Exportar dados para análise externa.",
      "Registrar evidências de acompanhamento de um ambiente.",
      "Compartilhar informações com equipes técnicas, clientes ou gestão.",
    ],
    pontosAtencao: [
      "Confirme ambiente, parâmetro e período antes de gerar o relatório.",
      "Prefira intervalos objetivos para obter arquivos mais úteis e organizados.",
    ],
  },
  {
    titulo: "Ocorrências",
    icone: <WarningAmberIcon color="warning" />,
    descricao:
      "Mostra eventos, alertas e situações que exigem atenção com base nos dados monitorados.",
    quandoUsar: [
      "Identificar situações fora do esperado.",
      "Acompanhar eventos relevantes e sua evolução.",
      "Priorizar atendimento e verificação técnica.",
    ],
    pontosAtencao: [
      "Use esta tela para triagem e priorização.",
      "Depois da identificação, consulte Histórico ou Dashboard para entender melhor o contexto.",
    ],
  },
  {
    titulo: "Estatísticas",
    icone: <InsightsIcon color="primary" />,
    descricao:
      "Apresenta indicadores consolidados do período selecionado, como médias, extremos e dispersão.",
    quandoUsar: [
      "Resumir o comportamento de um ambiente em um intervalo.",
      "Avaliar estabilidade, variação e consistência dos dados.",
      "Apoiar comparações técnicas e operacionais.",
    ],
    pontosAtencao: [
      "Utilize períodos curtos e representativos para análises mais rápidas e claras.",
      "Em consultas extensas, prefira dividir a análise em janelas menores.",
    ],
  },
  {
    titulo: "Análise IA",
    icone: <PsychologyIcon color="primary" />,
    descricao:
      "Realiza análise orientada por histórico do ambiente, tendência, baseline e recomendações operacionais.",
    quandoUsar: [
      "Investigar comportamento fora do padrão do ambiente.",
      "Obter leitura assistida com tendência e recomendação.",
      "Comparar o valor atual com o histórico recente e a faixa esperada.",
    ],
    pontosAtencao: [
      "A análise depende da qualidade e da quantidade de dados históricos disponíveis.",
      "Interprete a recomendação em conjunto com o contexto do ambiente monitorado.",
    ],
  },
  {
    titulo: "Assistência",
    icone: <HelpOutlineIcon color="primary" />,
    descricao:
      "Canal de suporte, contato comercial e orientações rápidas de uso do sistema.",
    quandoUsar: [
      "Falar com suporte técnico ou atendimento comercial.",
      "Consultar instruções básicas de uso do sistema.",
      "Ver como instalar o app no celular ou computador.",
    ],
    pontosAtencao: [
      "Ao solicitar suporte, informe o ambiente, a tela utilizada e o problema encontrado.",
      "Sempre que possível, envie prints ou uma descrição objetiva da ocorrência.",
    ],
  },
];

function ListaTopicos({ itens }) {
  return (
    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
      {itens.map((item) => (
        <Box component="li" key={item} sx={{ mb: 0.9 }}>
          <Typography variant="body2">{item}</Typography>
        </Box>
      ))}
    </Box>
  );
}

function BlocoPagina({ titulo, icone, descricao, quandoUsar, pontosAtencao, defaultExpanded = false }) {
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      sx={{
        borderRadius: 3,
    overflow: "hidden",
        bgcolor: "background.paper",
        border: (theme) =>
          `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)"}`,
        boxShadow: "none",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          {icone}
          <Box>
            <Typography fontWeight={700}>{titulo}</Typography>
            <Typography variant="body2" color="text.secondary">
              {descricao}
            </Typography>
          </Box>
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 0, pb: 2.5 }}>
        <Stack spacing={2}>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 3,
              bgcolor: (theme) =>
                theme.palette.mode === "dark" ? "rgba(255,255,255,0.02)" : "#fafafa",
            }}
          >
            <Typography fontWeight={700} sx={{ mb: 1 }}>
              Quando usar
            </Typography>
            <ListaTopicos itens={quandoUsar} />
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 3,
              bgcolor: (theme) =>
                theme.palette.mode === "dark" ? "rgba(255,255,255,0.02)" : "#fafafa",
            }}
          >
            <Typography fontWeight={700} sx={{ mb: 1 }}>
              Pontos de atenção
            </Typography>
            <ListaTopicos itens={pontosAtencao} />
          </Paper>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

export default function Assistencia() {
  const theme = useTheme();

  return (
    <Box sx={{ maxWidth: 980, mx: "auto", mt: 4, px: { xs: 1.5, sm: 2 }, pb: 4 }}>
      <Stack spacing={3}>
        <Paper
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 4,
            boxShadow: 3,
            bgcolor: theme.palette.mode === "dark" ? "#222" : "#f9f9fa",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <SupportAgentIcon color="primary" fontSize="large" />
              <Box>
                <Typography variant="h5" fontWeight={700}>
                  Assistência & Vendas
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Suporte técnico, orientações de uso e contato comercial.
                </Typography>
              </Box>
            </Stack>

            <Chip label="Atendimento • Seg a Sex • 8h às 18h" color="primary" variant="outlined" />
          </Stack>

          <Typography sx={{ mb: 2 }}>
            Precisa de suporte técnico ou deseja falar com nosso setor comercial? Entre em contato:
          </Typography>

          <Stack spacing={2} sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1.2} alignItems="center">
              <WhatsAppIcon color="success" fontSize="medium" />
              <Link
                href="https://wa.me/5516996462503"
                target="_blank"
                underline="hover"
                color="inherit"
                sx={{ fontSize: 17, fontWeight: 500 }}
              >
                (16) 99646-2503
              </Link>
            </Stack>

            <Stack direction="row" spacing={1.2} alignItems="center">
              <EmailIcon color="primary" fontSize="medium" />
              <Link
                href="mailto:contato@iseq.com.br"
                underline="hover"
                color="inherit"
                sx={{ fontSize: 17, fontWeight: 500 }}
              >
                contato@iseq.com.br
              </Link>
            </Stack>
          </Stack>

          <Typography variant="body2" color="text.secondary">
            Ao solicitar suporte, informe o ambiente, a tela utilizada e descreva o comportamento observado.
          </Typography>
        </Paper>

        <Paper
          sx={{
            p: { xs: 2.5, sm: 3 },
            borderRadius: 4,
            bgcolor: theme.palette.mode === "dark" ? "#23272f" : "#f6fbf9",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.2} sx={{ mb: 1 }}>
            <PhoneIphoneIcon color="primary" />
            <Typography variant="h6" fontWeight={700}>
              Como instalar o app no seu celular
            </Typography>
          </Stack>

          <Divider sx={{ my: 1.5, mb: 2 }} />

          <Stack spacing={2.5}>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                <AndroidIcon color="success" />
                <Typography fontWeight={600}>Android (Chrome)</Typography>
              </Stack>
              <Typography variant="body2" sx={{ ml: 4 }}>
                1. Acesse o sistema normalmente.<br />
                2. Toque no menu <b>(⋮)</b> no canto superior direito.<br />
                3. Selecione <b>“Instalar app”</b> ou <b>“Adicionar à tela inicial”</b>.
              </Typography>
            </Box>

            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                <AppleIcon sx={{ color: theme.palette.mode === "dark" ? "#fff" : "#111" }} />
                <Typography fontWeight={600}>iPhone / iOS (Safari)</Typography>
              </Stack>
              <Typography variant="body2" sx={{ ml: 4 }}>
                1. Acesse o sistema pelo Safari.<br />
                2. Toque em <b>Compartilhar</b> (quadrado com seta para cima).<br />
                3. Toque em <b>“Adicionar à Tela de Início”</b>.
              </Typography>
            </Box>

            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                <LanguageIcon color="action" />
                <Typography fontWeight={600}>Computador</Typography>
              </Stack>
              <Typography variant="body2" sx={{ ml: 4 }}>
                No Chrome, clique no botão <b>“Instalar”</b> na barra de endereços ou procure essa opção no menu do navegador.
              </Typography>
            </Box>
          </Stack>
        </Paper>

        <Paper
          sx={{
            p: { xs: 2.5, sm: 3 },
            borderRadius: 4,
            boxShadow: 2,
            bgcolor: theme.palette.mode === "dark" ? "#222" : "#ffffff",
          }}
        >
          <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
            Guia rápido das páginas do sistema
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            Use este guia para entender o objetivo de cada tela e escolher a melhor forma de consultar os dados.
          </Typography>

          <Stack spacing={1.5}>
            {GUIA_PAGINAS.map((item, index) => (
              <BlocoPagina
                key={item.titulo}
                {...item}
                defaultExpanded={index === 0}
              />
            ))}
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
}
